import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PseoPage } from "@/components/laneguide/PseoPage";
import { formatKr, parseAmountSlug } from "@/lib/pseo";
import {
  MIN_LENDERS_FOR_INDEX,
  amountsForCategory,
  cityAmountFor,
  getMatrixCategory,
  lendersForMatrix,
  neighbourAmounts,
  type MatrixCategory,
} from "@/lib/pseo-matrix";
import { TOP_CITIES, formatPopulation, getCity } from "@/lib/pseo-cities";
import { editorialFor, tierFaq } from "@/lib/pseo-editorial";

const BASE = "https://svensklaneguide.se";


function cityIsIndexable(cat: MatrixCategory) {
  return TOP_CITIES.length > 0 && lendersForMatrix(cat, cityAmountFor(cat)).length >= MIN_LENDERS_FOR_INDEX;
}

export const Route = createFileRoute("/$category/$amount")({
  loader: ({ params }) => {
    const cat = getMatrixCategory(params.category);
    if (!cat) throw notFound();

    const amount = parseAmountSlug(params.amount);
    if (amount) {
      if (!amountsForCategory(cat).includes(amount)) throw notFound();
      return { kind: "amount" as const, category: cat.slug, amount, city: null };
    }

    const city = TOP_CITIES.find((c) => c.slug === params.amount);
    if (city && cityIsIndexable(cat)) {
      return { kind: "city" as const, category: cat.slug, amount: cityAmountFor(cat), city: city.slug };
    }
    throw notFound();
  },
  head: ({ params, loaderData }) => {
    const cat = loaderData ? getMatrixCategory(loaderData.category) : null;
    if (!cat || !loaderData) {
      return { meta: [{ title: "Sidan hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const kr = formatKr(loaderData.amount);
    const url = `${BASE}/${params.category}/${params.amount}`;
    const city = loaderData.city ? getCity(loaderData.city) : null;

    const title = city
      ? `${cat.crumb} i ${city.name} – jämför långivare 2026 | Svensk Låneguide`
      : cat.title(kr);
    const description = city
      ? `Söker du ${cat.crumb.toLowerCase()} i ${city.name}? Jämför långivare som lånar ut i ${city.name}, se ränta, krav och månadskostnad – ansök online med BankID.`
      : cat.description(kr);
    const faq = city ? cat.faq(loaderData.amount, kr) : [...cat.faq(loaderData.amount, kr), ...tierFaq(loaderData.amount)];
    const thin = lendersForMatrix(cat, loaderData.amount).length < MIN_LENDERS_FOR_INDEX;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "robots", content: thin ? "noindex, follow" : "index, follow" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: BASE },
              {
                "@type": "ListItem",
                position: 2,
                name: cat.crumb,
                item: `${BASE}/kategori/${cat.slug}`,
              },
              { "@type": "ListItem", position: 3, name: city ? city.name : kr, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: MatrixPage,
});

function MatrixPage() {
  const data = Route.useLoaderData();
  const cat = getMatrixCategory(data.category)!;
  const [amount, setAmount] = useState(data.amount);
  const lenders = lendersForMatrix(cat, amount);
  const kr = formatKr(data.amount);
  const city = data.city ? getCity(data.city) : null;

  if (city) {
    const related = [
      ...TOP_CITIES.filter((c) => c.slug !== city.slug)
        .slice(0, 9)
        .map((c) => ({ label: `${cat.crumb} i ${c.name}`, to: `/${cat.slug}/${c.slug}` })),
      { label: `Lån i ${city.name}`, to: `/lan/${city.slug}` },
      { label: cat.crumb, to: `/kategori/${cat.slug}` },
    ];

    return (
      <PseoPage
        h1={`${cat.crumb} i ${city.name} – jämför långivare`}
        intro={`Söker du ${cat.crumb.toLowerCase()} i ${city.name}? Nedan jämför du de långivare som erbjuder ${cat.label} och lånar ut till privatpersoner i hela ${city.county}. Ansökan sker digitalt med BankID.`}
        amount={amount}
        onAmountChange={setAmount}
        amountEditable
        lenders={lenders}
        crumbs={[
          { label: "Hem", to: "/" },
          { label: cat.crumb, to: `/kategori/${cat.slug}` },
          { label: city.name },
        ]}
        faq={cat.faq(amount, formatKr(amount))}
        editorial={editorialFor(amount, { context: `${cat.label}`, place: city.name })}
        epi={`pseo-${cat.slug}-stad`}
        relatedTitle={`${cat.crumb} på andra orter`}
        related={related}
        extraContent={
          <section className="mx-auto max-w-3xl px-4 pt-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-black text-foreground">
              {cat.crumb} för dig som bor i {city.name}
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              {city.note} Med cirka {formatPopulation(city.population)} invånare är {city.name} en av landets största
              orter, och samtliga långivare på sidan lånar ut här på exakt samma villkor som i övriga Sverige.
            </p>
            <h3 className="mt-6 font-display text-lg font-bold text-foreground">Räkna innan du ansöker</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Använd kalkylatorn högst upp eller vår{" "}
              <Link to="/guider/lanekalkyl" className="text-primary underline underline-offset-2">
                lånekalkylator
              </Link>{" "}
              för att se vad lånet kostar i månaden innan du skickar in ansökan.
            </p>
          </section>
        }
      />
    );
  }

  const related = [
    ...neighbourAmounts(cat, data.amount).map((a) => ({
      label: `${cat.crumb} ${formatKr(a)}`,
      to: `/${cat.slug}/${a}-kr`,
    })),
    { label: `Alla lån på ${kr}`, to: `/lan/${data.amount}-kr` },
    { label: cat.crumb, to: `/kategori/${cat.slug}` },
  ];

  return (
    <PseoPage
      h1={cat.h1(kr)}
      intro={cat.intro(kr)}
      amount={amount}
      onAmountChange={setAmount}
      amountEditable
      lenders={lenders}
      crumbs={[{ label: "Hem", to: "/" }, { label: cat.crumb, to: `/kategori/${cat.slug}` }, { label: kr }]}
      faq={[...cat.faq(data.amount, kr), ...tierFaq(data.amount)]}
      editorial={editorialFor(data.amount, { context: cat.label })}
      epi={`pseo-${cat.slug}`}
      relatedTitle={`Andra belopp för ${cat.crumb.toLowerCase()}`}
      related={related}
      extraContent={
        <section className="mx-auto max-w-3xl px-4 pt-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-black text-foreground">
            En ansökan – flera bud på {kr}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Ansöker du via en låneförmedlare skickas din ansökan till flera banker samtidigt, men bara en
            kreditupplysning tas. Det gör att du kan jämföra faktiska erbjudanden på {kr} utan att din kreditvärdighet
            påverkas av varje enskild förfrågan. Räkna på totalbeloppet i vår{" "}
            <Link to="/guider/lanekalkyl" className="text-primary underline underline-offset-2">
              lånekalkylator
            </Link>{" "}
            innan du signerar.
          </p>
        </section>
      }
    />
  );
}
